import { useEffect, useState } from 'react';
import {
  getGuardByEmployee,
  getTopBaseOperatorByEmployee,
  getChppOperatorByEmployee,
  getPrivatePointOperatorByEmployee,
} from 'service/RoleService';
import { getTopBaseBranches, getChppBranches, getPrivatePointBranches } from 'service/AdminPanelService';
import { getDriverCabinet } from 'service/DriverRegistrationService';
import userInfo from 'utils/userInfo';

/**
 * Хук для определения роли пользователя (охранник / оператор базы / ТЭЦ / соц.объект)
 * и загрузки списка филиалов оператора.
 */
const useUserRoles = () => {
  const user = userInfo();

  const [isGuard, setIsGuard] = useState(false);
  const [isTopBaseOperator, setIsTopBaseOperator] = useState(false);
  const [isChppOperator, setIsChppOperator] = useState(false);
  const [isPrivatePointOperator, setIsPrivatePointOperator] = useState(false);
  const [isDriverChecked, setIsDriverChecked] = useState(false);
  const [operatorBranches, setOperatorBranches] = useState([]);
  const [roleLoading, setRoleLoading] = useState(true);
  const [roleError, setRoleError] = useState(null);

  // Проверка ролей с экспоненциальным retry
  useEffect(() => {
    let isMounted = true;
    let retryTimeout = null;

    const checkRoles = async (retryCount = 0) => {
      if (!isMounted) return;
      setRoleLoading(true);
      setRoleError(null);

      try {
        if (user?.is_admin_of || user?.is_report_of) {
          setIsGuard(false);
          setIsTopBaseOperator(false);
          setIsChppOperator(false);
          setIsPrivatePointOperator(false);
          setIsDriverChecked(false);
          setRoleLoading(false);
          return;
        }
        const guardRes = await getGuardByEmployee();
        setIsGuard(!!guardRes?.data);

        const topBaseRes = await getTopBaseOperatorByEmployee();
        setIsTopBaseOperator(topBaseRes?.data || false);

        const chppRes = await getChppOperatorByEmployee();
        setIsChppOperator(chppRes?.data || false);

        const privatePointRes = await getPrivatePointOperatorByEmployee();
        setIsPrivatePointOperator(privatePointRes?.data || false);

        // Check if user is a driver (self-registered)
        const isAnyRole = guardRes?.data || topBaseRes?.data || chppRes?.data || privatePointRes?.data;
        if (!isAnyRole) {
          try {
            const driverRes = await getDriverCabinet();
            setIsDriverChecked(!!(driverRes?.data?.driver_checked || driverRes?.data?.driver));
          } catch {
            setIsDriverChecked(false);
          }
        } else {
          setIsDriverChecked(false);
        }

        setRoleLoading(false);
      } catch (e) {
        console.error('Ошибка проверки роли пользователя, попытка:', retryCount + 1, e);
        setRoleError('Ошибка проверки роли пользователя');
        setRoleLoading(false);

        if (isMounted) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 2000);
          retryTimeout = setTimeout(() => {
            if (isMounted) checkRoles(retryCount + 1);
          }, delay);
        }
      }
    };

    checkRoles();

    return () => {
      isMounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Загрузка филиалов оператора
  useEffect(() => {
    const loadOperatorBranches = async () => {
      let baseId = null;
      let baseType = null;

      if (isTopBaseOperator && isTopBaseOperator.top_base) {
        baseId = isTopBaseOperator.top_base;
        baseType = 'topbase';
      } else if (isChppOperator && isChppOperator.chpp) {
        baseId = isChppOperator.chpp;
        baseType = 'chpp';
      } else if (isPrivatePointOperator && isPrivatePointOperator.private_point) {
        baseId = isPrivatePointOperator.private_point;
        baseType = 'privatepoint';
      }

      if (baseId && baseType) {
        try {
          let res;
          if (baseType === 'topbase') res = await getTopBaseBranches(baseId);
          else if (baseType === 'chpp') res = await getChppBranches(baseId);
          else if (baseType === 'privatepoint') res = await getPrivatePointBranches(baseId);
          setOperatorBranches(Array.isArray(res.data.results) ? res.data.results : []);
        } catch (e) {
          console.error('Error loading branches:', e);
          setOperatorBranches([]);
        }
      } else {
        setOperatorBranches([]);
      }
    };
    loadOperatorBranches();
  }, [isTopBaseOperator, isChppOperator, isPrivatePointOperator]);

  return {
    user,
    isGuard,
    isTopBaseOperator,
    isChppOperator,
    isPrivatePointOperator,
    isDriverChecked,
    operatorBranches,
    roleLoading,
    roleError,
  };
};

export default useUserRoles;
